import { useCallback, useState } from 'react';

export type CampaignMode = 'none' | 'existing' | 'new';

/**
 * The campaign half of the New Taxonomy form.
 *
 * Six values that only ever move together, behind one hook — the shape this app
 * already uses for form state (`useBriefEditorForm` and the `features/*\/model`
 * hooks). Deliberately not a `useReducer`: the only two reducers in this app
 * drive genuine multi-phase lifecycles, and four of the six values here are
 * plain inputs that never interact with the other two.
 *
 * The one real invariant lives in `markCampaignCreated`. Everything else is a
 * setter.
 */
export function useCampaignSelection() {
  const [mode, setMode] = useState<CampaignMode>('none');
  const [existingCampaignId, setExistingCampaignId] = useState('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  /**
   * A campaign already created during THIS open, so a retry reuses it.
   *
   * The dataset create can fail after the campaign create succeeded — a name
   * clash is the ordinary way — and the modal stays open for the user to fix the
   * name. Without this, the second Create makes a SECOND campaign with the same
   * name, and campaign names are deliberately not unique, so nothing downstream
   * would refuse it.
   */
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);

  const startCampaignCreation = useCallback(() => setCreating(true), []);

  /**
   * Records the created campaign and DELIBERATELY LEAVES `creating` TRUE.
   *
   * This is the whole reason the pair is behind named setters rather than two
   * bare `useState`s. Clearing `creating` here would re-enable Create for the
   * window between the campaign landing and the taxonomy submit — the parent's
   * own `submitting` has not flipped yet — and a second click in that window
   * fires a second `onCreate`. `finishCampaignCreation` is the only success-path
   * clear, and it is called in the same synchronous batch as the submit.
   */
  const markCampaignCreated = useCallback((id: string) => setCreatedCampaignId(id), []);

  /** Terminal for both the failure path and the submit path. */
  const finishCampaignCreation = useCallback(() => setCreating(false), []);

  const reset = useCallback(() => {
    setMode('none');
    setExistingCampaignId('');
    setNewName('');
    setNewDescription('');
    setCreatedCampaignId(null);
    /*
     * `creating` is NOT reset here. This runs when the modal closes, which can
     * happen while a campaign request is still in flight; the continuation that
     * is still running owns clearing it, and always does.
     */
  }, []);

  return {
    mode,
    setMode,
    existingCampaignId,
    setExistingCampaignId,
    newName,
    setNewName,
    newDescription,
    setNewDescription,
    creating,
    createdCampaignId,
    startCampaignCreation,
    markCampaignCreated,
    finishCampaignCreation,
    reset,
  };
}
